import * as React from 'react';
import { AccentButton } from '../AccentButton';
import { AccentSpinner } from '../AccentSpinner';
import { AccentCheckUnbound } from '../unbound/AccentCheckUnbound';
import { t } from '../../services/HelperService';
import { getUser } from '../../services/UserService';
import {
  REPORT_DATE_FROM_TOKEN,
  REPORT_DATE_TO_TOKEN,
  ReportDateRangePicker,
  computeReportDateRange,
  formatReportDateRangeLabel,
  usesReportDateRange,
} from './ReportDateRangePicker';
import { patchCustomizationPrefs, resolveInitialDateRange } from '../customizations/customizationUserPrefs';

const safeString = (v) => (v ?? '').toString();

/**
 * CDN hosts report documents may load libraries from when the user allows external
 * libraries. Mirrors AdhocReportSessionProfile.AllowedExternalHosts (C#): the server
 * validator rejects other hosts, and this CSP blocks them at render time — two
 * independent enforcement layers.
 */
const ALLOWED_CDN_HOSTS = [
  'https://cdn.jsdelivr.net',
  'https://unpkg.com',
  'https://cdnjs.cloudflare.com',
];

const buildCsp = (allowExternalLibraries) => {
  if (allowExternalLibraries) {
    const cdns = ALLOWED_CDN_HOSTS.join(' ');
    return `default-src 'none'; script-src 'unsafe-inline' 'unsafe-eval' ${cdns}; style-src 'unsafe-inline' ${cdns} https://fonts.googleapis.com; font-src data: https://fonts.gstatic.com; img-src data: blob:;`;
  }
  return "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; font-src data:; img-src data: blob:;";
};

/**
 * Bridge script injected into the report document: forwards uncaught script errors to
 * the parent window so the dialog can show them in the conversation. The frame is an
 * opaque origin (sandbox without allow-same-origin), so postMessage is the only channel.
 * It also suppresses the frame's context menu, as the other previews do: the preview
 * snip is not supported here (the parent cannot see into the sandbox), and a right-drag
 * that does nothing should not pop the browser menu either.
 */
const ERROR_BRIDGE = '<script>window.addEventListener("error",function(e){try{parent.postMessage({__adhocReportError:(e&&e.message)||"Script error"},"*")}catch(_){}});window.addEventListener("contextmenu",function(e){e.preventDefault()});<\/script>';

/**
 * Serializes the report environment (locale/timezone/currency plus the current date
 * range) into a script that sets window.__reportEnv — the contract the server profile's
 * prompt rules advertise to the report author: all currency/number/date FORMATTING
 * happens in the document's own browser script (full Intl) using these values, because
 * the report script itself runs server-side in Jint, which has no Intl support.
 */
const buildReportEnvScript = (reportEnv) => {
  if (!reportEnv || typeof reportEnv !== 'object') return '';
  try {
    // \u003c-escape so report data can never close the script tag early.
    const json = JSON.stringify(reportEnv).replace(/</g, '\\u003c');
    return `<script>window.__reportEnv=${json};</script>`;
  } catch {
    return '';
  }
};

/**
 * Prepares the AI-generated HTML document for the sandboxed frame: injects the
 * Content-Security-Policy for the session's external-libraries choice, the error
 * bridge, and the report environment as the FIRST head content (CSP must precede any
 * resource reference).
 */
export const buildReportSrcDoc = (html, allowExternalLibraries, reportEnv) => {
  const doc = safeString(html);
  if (!doc.trim()) return '';
  const inject = `<meta http-equiv="Content-Security-Policy" content="${buildCsp(allowExternalLibraries)}">${ERROR_BRIDGE}${buildReportEnvScript(reportEnv)}`;

  const headMatch = /<head\b[^>]*>/i.exec(doc);
  if (headMatch) {
    const at = headMatch.index + headMatch[0].length;
    return `${doc.slice(0, at)}${inject}${doc.slice(at)}`;
  }
  const htmlMatch = /<html\b[^>]*>/i.exec(doc);
  if (htmlMatch) {
    const at = htmlMatch.index + htmlMatch[0].length;
    return `${doc.slice(0, at)}<head>${inject}</head>${doc.slice(at)}`;
  }
  // Fragment fallback (the server validator normally rejects these before we get here).
  return `<!DOCTYPE html><html><head>${inject}</head><body>${doc}</body></html>`;
};

/** Parses an output's ValueJson (a JSON string literal) back to the HTML document. */
const parseReportHtml = (valueJson) => {
  const raw = safeString(valueJson).trim();
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : '';
  } catch {
    return '';
  }
};

/**
 * The ad-hoc report profile's preview: renders the generated HTML document in a
 * sandboxed iframe, with the third-party-libraries disclaimer and consent checkbox that
 * drive the session's allowExternalLibraries option (prompt rules, server validation,
 * and the frame CSP all follow this choice).
 *
 * The frame document also receives window.__reportEnv (user locale/timezone/currency
 * plus the current date range) so the report formats values with the browser's full
 * Intl. When any query references the {{report.dateFrom}}/{{report.dateTo}} tokens, a
 * date-range parameter bar appears: changing it feeds the computed instants into the
 * session's template values (via onRefresh's patch argument) and re-runs the queries.
 */
export const AdhocReportPreview = ({ queries, outputs, busy, sessionOptions, templateValues, onTemplateValuesChange, onSessionOptionsChange, onRefresh, onDefect, designMode = true, stateKey }) => {
  const allowExternal = !!sessionOptions?.allowExternalLibraries;

  const user = getUser();
  const locale = safeString(user?.Locale) || 'en-AU';
  const timeZone = safeString(user?.IANATimeZoneID) || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currency = safeString(user?.CurrencyCode) || 'AUD';

  // The date-range choice is stored as the PRESET (plus custom dates), not the computed
  // instants: "MTD" must mean MTD at every refresh, so instants are recomputed on use.
  // Seeded from the viewer's remembered choice, then the author's saved default.
  const [dateRange, setDateRange] = React.useState(() => resolveInitialDateRange(stateKey, sessionOptions));
  const showDateRange = usesReportDateRange(queries);
  // Memoized: a fresh range object per render would churn the srcDoc memo below and
  // remount the iframe on every render.
  const computedRange = React.useMemo(
    () => (showDateRange
      ? computeReportDateRange(dateRange.preset, timeZone, dateRange.customFrom, dateRange.customTo)
      : null),
    [showDateRange, dateRange, timeZone]
  );
  const rangeLabel = computedRange ? formatReportDateRangeLabel(dateRange.preset, computedRange, locale) : '';

  // First time the queries start using the date tokens, seed the session's template
  // values with the default preset so manual refreshes and the AI's turns run against
  // the same window the bar displays (instead of the server's silent fallback).
  // Live mode always seeds: the definition carries the AUTHOR's instants from save
  // time, and the run must match the (possibly restored) range the bar displays.
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (!showDateRange || seededRef.current) return;
    seededRef.current = true;
    if (!computedRange) return;
    const hasValues = !!safeString(templateValues?.[REPORT_DATE_FROM_TOKEN]).trim() &&
      !!safeString(templateValues?.[REPORT_DATE_TO_TOKEN]).trim();
    if (designMode && hasValues) return;
    onTemplateValuesChange?.({
      [REPORT_DATE_FROM_TOKEN]: computedRange.from.toISOString(),
      [REPORT_DATE_TO_TOKEN]: computedRange.to.toISOString(),
    });
    // Seeding is a one-shot on first token appearance, not a value-tracking effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDateRange]);

  const onDateRangeChange = (next) => {
    setDateRange(next);
    // Design mode: the author's last selection becomes the customization's default
    // (preset only — dates are the viewer's business). Live mode: the viewer's own
    // selection is remembered for their next visit.
    if (designMode) onSessionOptionsChange?.({ defaultDateRange: safeString(next.preset) });
    else if (stateKey) patchCustomizationPrefs(stateKey, { dateRange: next });
    const range = computeReportDateRange(next.preset, timeZone, next.customFrom, next.customTo);
    if (!range) return; // incomplete custom range — wait for both dates
    // The patch rides the refresh call so the re-run uses THESE values (the host merges
    // them into its template-values state, which React has not committed yet).
    onRefresh?.({
      [REPORT_DATE_FROM_TOKEN]: range.from.toISOString(),
      [REPORT_DATE_TO_TOKEN]: range.to.toISOString(),
    });
  };

  const reportOutput = (outputs ?? []).find((o) => !!parseReportHtml(o.valueJson));
  const html = parseReportHtml(reportOutput?.valueJson);
  const reportEnv = React.useMemo(() => ({
    locale,
    timeZone,
    currency,
    dateFrom: computedRange ? computedRange.from.toISOString() : null,
    dateTo: computedRange ? computedRange.to.toISOString() : null,
    dateRangeLabel: rangeLabel || null,
  }), [locale, timeZone, currency, computedRange, rangeLabel]);
  const srcDoc = React.useMemo(() => buildReportSrcDoc(html, allowExternal, reportEnv), [html, allowExternal, reportEnv]);

  // Frame runtime errors surface in the conversation, once per message per document.
  const reportedRef = React.useRef(new Set());
  React.useEffect(() => { reportedRef.current = new Set(); }, [srcDoc]);
  React.useEffect(() => {
    const onMessage = (e) => {
      const msg = safeString(e?.data?.__adhocReportError).trim();
      if (!msg || reportedRef.current.has(msg)) return;
      reportedRef.current.add(msg);
      onDefect?.(`${t('The report page hit a script error while rendering')}: ${msg}`);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onDefect]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0 }}>
      {/* The consent choice is an AUTHORING decision baked into the saved definition;
          live viewers (dashboard widget, sidebar Reports page) just honor it. */}
      {designMode && (
        <div style={{ border: '1px solid #ffe0b2', background: '#fff8e1', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('Third-party libraries')}</div>
          <div style={{ marginBottom: 6 }}>
            {t('The report can optionally load charting/styling libraries from public CDNs (jsdelivr, unpkg, cdnjs). That code is fetched from the internet and runs inside the sandboxed preview — only enable this if you are comfortable with it. When disabled, the report is fully self-contained.')}
          </div>
          <AccentCheckUnbound
            tagName="allowExternalLibraries"
            label={t('Allow third-party libraries from public CDNs')}
            disabled={busy}
            defaultValue={allowExternal}
            onChange={(e) => onSessionOptionsChange?.({ allowExternalLibraries: !!e.checked })}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <AccentButton outline size="sm" disabled={busy} onClick={(e) => { onRefresh?.(); e?.promise?.(); }}>
          {t('Refresh report')}
        </AccentButton>
        {showDateRange ? (
          <ReportDateRangePicker
            value={dateRange}
            disabled={busy}
            timeZone={timeZone}
            locale={locale}
            onChange={onDateRangeChange}
          />
        ) : (
          <span style={{ fontSize: 11, opacity: 0.6 }}>{t('Re-runs the queries and re-renders the report with fresh data.')}</span>
        )}
      </div>

      <div style={{ position: 'relative', flex: 1, minHeight: 0, border: '1px solid #dee2e6', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
        {srcDoc ? (
          <iframe
            title={t('Report preview')}
            sandbox="allow-scripts"
            srcDoc={srcDoc}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        ) : (
          <div style={{ padding: 16, fontSize: 13, opacity: 0.7 }}>
            {t('No report yet — describe the report you want in the conversation (e.g. "top 10 customers by sales amount as a table and pie chart").')}
          </div>
        )}
        {busy && (
          // The stale report stays visible under a translucent veil while the queries
          // re-run — reads as "updating", not a flash to blank.
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.6)',
            zIndex: 2,
          }}>
            <AccentSpinner />
          </div>
        )}
      </div>
    </div>
  );
};
